import axios from 'axios'
import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { secondaryFormatter } from './utils'
import Grid from '@material-ui/core/Grid'
import UploadSection from './UploadSection'
import RegularColumnSelection from '../components/RegularColumnSelection'
import { hasAllRequiredColumnValues } from './SourceDataMethods'
import LastUploadedSection from '../components/LastUploadedSection'
import { API_URL } from '../../../config'
import { firstLetterUpper } from '../../../util/stringTransform'
import toast from 'react-hot-toast'
import { logError } from '../../../util/logger'

const useStyles = makeStyles(() => ({
  breakLine: {
    margin: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  root: {},
}))

const BOMSourceData = ({ uploadSummary, refetchData, setExpand }) => {
  const classes = useStyles()

  const currentColumnRef = uploadSummary?.columnRef
  const currentSheetSelected = uploadSummary?.sheetNameToSelect
  const currentUploadedFile = uploadSummary?.fileName
  const lastImported = uploadSummary?.lastImported
  const fileStructure = uploadSummary?.fileStructure
  const fileToken = uploadSummary?.fileToken
  const status = uploadSummary?.status
  const statusMessage = uploadSummary?.statusMessage

  const [currentColumnList, setCurrentColumnList] = useState(currentSheetSelected ? secondaryFormatter(fileStructure, currentSheetSelected) : [])
  const [sheet, setSheet] = useState(currentSheetSelected || '')
  const [partNumber, setPartNumber] = useState(currentColumnRef?.partNumber || '')
  const [level, setLevel] = useState(currentColumnRef?.level || '')
  const [quantity, setQuantity] = useState(currentColumnRef?.quantity || '')
  const [uom, setUOM] = useState(currentColumnRef?.unitOfMeasurement || '')
  const [desc, setDesc] = useState(currentColumnRef?.description || '')
  const [partType, setPartType] = useState(currentColumnRef?.partType || '')
  const [disableSave, setDisableSave] = useState(true)

  const setColumnData = (inputSheet, sheet) => {
    setCurrentColumnList(secondaryFormatter(inputSheet.fileStructure, sheet))
  }

  const changeSheet = (sheetName) => {
    if (sheetName === '') {
      setCurrentColumnList([])
    } else setColumnData(uploadSummary, sheetName)
    setSheet(sheetName)
  }

  const criticalColumnConfig = [
    {
      label: 'Part Number',
      ref: partNumber,
      refSet: setPartNumber,
      required: true,
    },
    {
      label: 'Level',
      ref: level,
      refSet: setLevel,
      required: true,
    },
    {
      label: 'Description',
      ref: desc,
      refSet: setDesc,
      required: true,
    },
    {
      label: 'Quantity',
      ref: quantity,
      refSet: setQuantity,
      required: true,
    },
    {
      label: 'Unit of Measurement',
      ref: uom,
      refSet: setUOM,
      required: false,
    },
    {
      label: 'Part Type',
      ref: partType,
      refSet: setPartType,
      required: false,
    },
  ]

  const allRequiredFieldsSelected = hasAllRequiredColumnValues(criticalColumnConfig)

  const handleFieldChange = (setStateFn, value) => {
    setStateFn(value)
    if (disableSave) setDisableSave(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const setBOMColumnRef = `${API_URL}/api/dataSource/v1/setBOMColumnRef`
    const data = {
      fileToken: fileToken,
      cleanImport: true,
      sheetName: sheet,
      columnRef: {
        partNumber: partNumber,
        level: level,
        quantity: quantity,
      },
    }
    if (uom) data.columnRef.unitOfMeasurement = uom
    if (desc) data.columnRef.description = desc
    if (partType) data.columnRef.partType = partType

    const mainToast = toast.loading(<div>{`We're taking a look at your BOM and refreshing your current relevant inventory. This may take a minute...`}</div>)
    const updateBOMColumnRef = axios
      .post(setBOMColumnRef, data)
      .then(async () => {
        toast.success(<b>Updated!</b>, {
          id: mainToast
        });
        await refetchData(true)
        setDisableSave(true)
        setExpand(false);
      })
      .catch((err) => {
        toast.error(err.response.data.message, {
          id: mainToast
        });
        logError(err.message, err.stack)
      })
  }

  return (
    <Grid container spacing={2} direction='column' className={classes.root}>
      <UploadSection uploadDataType='billOfMaterials' refetchSourceInfoFn={refetchData} setExpand={setExpand} uploadSummary={uploadSummary} />
      <hr className={classes.breakLine} />
      <LastUploadedSection
        fileName={currentUploadedFile}
        lastImported={lastImported}
        status={firstLetterUpper(status)}
        statusMessage={statusMessage}
        label={'BOM'}
      />
      <hr className={classes.breakLine} />
      <RegularColumnSelection
        config={criticalColumnConfig}
        changeSheet={changeSheet}
        currentSheet={sheet}
        fileStructure={fileStructure}
        currentColumnList={currentColumnList}
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmit}
        allRequiredFieldsSelected={allRequiredFieldsSelected}
        disableSave={disableSave}
      />
    </Grid>
  )
}

export default BOMSourceData
